import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AssetSearchFormAdvanced } from 'page-components/asset/SearchFormAdvanced'
import { AssetList } from 'page-components/asset/List'
import { LoaderBar } from 'components'
import {
    useAsset,
    useAssetCategory,
    useAssetLocation,
} from 'hooks/asset-manager'
import { useAuth } from 'context/user'
import { getAssetPermissions } from 'utility/asset-permissions'

export const AssetSearchScreen = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)
    const { assets, loading, fetchAssets, deleteAsset } = useAsset()
    const { categories, fetchCategories } = useAssetCategory()
    const { locations, fetchLocations } = useAssetLocation()

    const [searching, setSearching] = useState(false)
    const [searchResults, setSearchResults] = useState(null)
    const [hasSearched, setHasSearched] = useState(false)
    const [activeSearchParams, setActiveSearchParams] = useState(null)
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 25,
    })

    useEffect(() => {
        fetchCategories()
        fetchLocations()
        fetchAssets({ OnlyActive: false })
    }, [fetchAssets, fetchCategories, fetchLocations])

    const categoriesForUi = Array.isArray(categories) ? categories : []
    const locationsForUi = Array.isArray(locations) ? locations : []
    const assetsForUi = Array.isArray(assets) ? assets : []

    const locationOptions = useMemo(
        () =>
            locationsForUi.map(location => ({
                key: location.AssetLocationId,
                text: location.LocationName,
                value: String(location.AssetLocationId),
            })),
        [locationsForUi],
    )

    const categoryOptions = useMemo(
        () =>
            categoriesForUi.map(category => ({
                key: category.AssetCategoryId,
                text: category.CategoryName,
                value: String(category.AssetCategoryId),
            })),
        [categoriesForUi],
    )

    const handleSearch = async (searchParams) => {
        setHasSearched(true)
        setSearching(true)
        setActiveSearchParams(searchParams)
        setFilter({ PageNumber: 1, PageSize: 25 })
        setSearching(false)
    }

    const handleNewSearch = () => {
        setSearchResults(null)
        setHasSearched(false)
        setActiveSearchParams(null)
        setFilter({ PageNumber: 1, PageSize: 25 })
    }

    const handleExport = () => {
        // TODO: Implement export functionality
        console.log('Export search results:', searchResults)
    }

    const handleViewAsset = (item) => {
        const id = item.AssetId || item.assetId
        if (!id) return
        navigate(`/app/asset-management/${id}`, { state: { asset: item } })
    }

    const handleEditAsset = (item) => {
        const id = item.AssetId || item.assetId
        if (!id) return
        navigate(`/app/asset-management/${id}`, { state: { asset: item } })
    }

    const handleDeleteAsset = async (item) => {
        if (!permissions.canDeleteAsset) return
        const id = item.AssetId || item.assetId
        if (!id) return
        const success = await deleteAsset(id)
        if (success) {
            fetchAssets({ OnlyActive: false })
            if (activeSearchParams) {
                handleSearch(activeSearchParams)
            }
        }
    }

    const handleAssignInspection = (item) => {
        navigate('/app/asset-management/schedule-inspection', {
            state: { asset: item },
        })
    }

    const handleGenerateQR = (item) => {
        // TODO: Generate QR
        console.log('Generate QR:', item)
    }

    const handleExportAsset = (item) => {
        // TODO: Export asset
        console.log('Export asset:', item)
    }

    const pageChanged = (newFilter) => {
        setFilter(newFilter)
        // TODO: Replace with actual API call
    }

    useEffect(() => {
        if (!hasSearched || !activeSearchParams) return

        const keyword = String(activeSearchParams.Keyword || '').toLowerCase().trim()
        const categoryId = String(activeSearchParams.Category || '')
        const locationId = String(activeSearchParams.Location || '')
        const selectedStatuses = Array.isArray(activeSearchParams.Status)
            ? activeSearchParams.Status
            : []

        const filtered = assetsForUi.filter(asset => {
            const assetName = String(asset.AssetName || '').toLowerCase()
            const assetTag = String(asset.AssetTag || asset.AssetId || '').toLowerCase()
            const matchesKeyword =
                !keyword ||
                assetName.includes(keyword) ||
                assetTag.includes(keyword)

            const matchesCategory =
                !categoryId || String(asset.AssetCategoryId || '') === categoryId

            const matchesLocation =
                !locationId || String(asset.AssetLocationId || '') === locationId

            const mappedStatus = asset.IsActive ? 'Active' : 'Inactive'
            const matchesStatus =
                selectedStatuses.length === 0 ||
                selectedStatuses.includes(mappedStatus)

            return (
                matchesKeyword &&
                matchesCategory &&
                matchesLocation &&
                matchesStatus
            )
        })
        setSearchResults(filtered)
    }, [assetsForUi, hasSearched, activeSearchParams])

    // Get paginated + mapped results
    const getPaginatedResults = () => {
        if (!searchResults || searchResults.length === 0) return []
        const startIndex = (filter.PageNumber - 1) * filter.PageSize
        const endIndex = startIndex + filter.PageSize
        return searchResults.slice(startIndex, endIndex).map(asset => ({
            ...asset,
            assetId: asset.AssetTag || String(asset.AssetId || ''),
            assetName: asset.AssetName || '',
            category:
                categoriesForUi.find(
                    item => item.AssetCategoryId === asset.AssetCategoryId,
                )?.CategoryName || '-',
            location:
                locationsForUi.find(
                    item => item.AssetLocationId === asset.AssetLocationId,
                )?.LocationName || '-',
            status: asset.IsActive ? 'Active' : 'Inactive',
            assignedTo: asset.AssignedTo || 'Unassigned',
        }))
    }

    return (
        <div style={{ padding: '20px' }}>
            {loading && <LoaderBar content="Loading assets..." />}
            {searching && <LoaderBar content="Searching..." />}
            <AssetSearchFormAdvanced
                onSearch={handleSearch}
                onNewSearch={handleNewSearch}
                onExport={handleExport}
                locationOptions={locationOptions}
                categoryOptions={categoryOptions}
                searchResults={hasSearched ? searchResults : null}
                loading={loading || searching}
            />
            {hasSearched &&
                searchResults &&
                searchResults.length > 0 &&
                !loading &&
                !searching && (
                <div className="my-3">
                    <AssetList
                        data={getPaginatedResults()}
                        filter={filter}
                        pageChanged={pageChanged}
                        totalCount={searchResults.length}
                        onView={handleViewAsset}
                        onEdit={handleEditAsset}
                        onDelete={handleDeleteAsset}
                        onAssignInspection={handleAssignInspection}
                        onGenerateQR={handleGenerateQR}
                        onExport={handleExportAsset}
                    />
                </div>
            )}
        </div>
    )
}
